import { Loader2, Mic, MicOff, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/utils';

interface VoiceInputActionButtonProps {
  disabled?: boolean;
  error: string | null;
  getErrorMessage?: (error: string) => string;
  handleCancelClick: () => void;
  handleConfirmClick: () => void;
  handleVoiceButtonClick: () => void;
  hasPermission?: boolean | null;
  isButtonDisabled?: boolean;
  isListening: boolean;
  isProcessing: boolean;
  isSupported: boolean;
}

export function VoiceInputActionButton({
  disabled,
  error,
  getErrorMessage,
  handleCancelClick,
  handleConfirmClick,
  handleVoiceButtonClick,
  hasPermission,
  isButtonDisabled,
  isListening,
  isProcessing,
  isSupported,
}: VoiceInputActionButtonProps) {
  const { t } = useTranslation();

  // Tooltip content based on current state
  const getTooltipContent = () => {
    if (!isSupported) {
      return t('assistant.voiceInput.unsupported');
    }

    if (error === 'not-allowed' || hasPermission === false) {
      return t('assistant.voiceInput.permissionDenied');
    }

    if (error) {
      return getErrorMessage ? getErrorMessage(error) : t('assistant.voiceInput.genericError');
    }

    if (disabled) {
      return t('assistant.voiceInput.disabled');
    }

    if (isProcessing) {
      return t('assistant.voiceInput.processing');
    }

    if (isListening) {
      return t('assistant.voiceInput.recording');
    }

    return t('assistant.voiceInput.dictate');
  };

  // Aria label based on current state
  const getAriaLabel = () => {
    if (isListening) {
      return t('assistant.voiceInput.stopRecording');
    }
    if (isProcessing) {
      return t('assistant.voiceInput.cancelProcessing');
    }
    return t('assistant.voiceInput.start');
  };

  if (isProcessing) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block">
            <Button
              aria-label={t('assistant.voiceInput.processing')}
              className="border-border-subtle flex h-6 w-6 items-center justify-center rounded-full p-0"
              disabled
              size="icon"
              variant="outline"
            >
              <Loader2 className="text-primary h-5 w-5 animate-spin" />
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getTooltipContent()}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isListening) {
    return (
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-block">
              <Button
                aria-label={t('assistant.voiceInput.cancelRecording')}
                className="hover:bg-elevated-subtle border-highlight h-6 w-6 rounded-full p-0"
                onClick={(e) => {
                  e.preventDefault();
                  handleCancelClick();
                }}
                size="icon"
                variant="outline"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('assistant.voiceInput.cancelRecording')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-block">
              <Button
                aria-label={t('assistant.voiceInput.confirmRecording')}
                className="border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 flex h-6 w-6 items-center justify-center rounded-full p-0"
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmClick();
                }}
                size="icon"
              >
                <Mic className="text-destructive-foreground h-5 w-5 animate-pulse" />
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('assistant.voiceInput.stopAndTranscribe')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-block">
          <Button
            aria-label={getAriaLabel()}
            className={cn(
              'h-6 w-6 rounded-full border-highlight p-0 transition-all duration-200 hover:bg-elevated-subtle',
              {
                'border-destructive bg-destructive hover:bg-destructive/90': isListening,
                'cursor-not-allowed opacity-50': isButtonDisabled ?? disabled ?? !isSupported,
              },
            )}
            disabled={isButtonDisabled ?? disabled ?? !isSupported}
            onClick={(e) => {
              e.preventDefault();
              handleVoiceButtonClick();
            }}
            size="icon"
            variant="outline"
          >
            {error ? (
              <MicOff className="text-neutral-mid h-5 w-5" />
            ) : (
              <Mic
                className={cn('h-5 w-5 text-neutral-mid', {
                  'text-tertiary': isSupported && !error,
                })}
              />
            )}
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{getTooltipContent()}</p>
      </TooltipContent>
    </Tooltip>
  );
}
